import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { DatePipe } from '@angular/common';
import { DomSanitizer, type SafeHtml } from '@angular/platform-browser';
import { TranslateModule } from '@ngx-translate/core';
import { DialogModule } from 'primeng/dialog';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import type { CommentEdit } from '@tourfold/libraries/tf-api-client-public-v2';
import { renderCommentBody } from '../services/comment-body-renderer';
import { CommentsService } from '../services/comments.service';

/**
 * Lazy-loaded edit-history modal. Opens with `[visible]="true"` and emits `visibleChange` so the
 * parent can drive PrimeNG's two-way binding pattern. The first time the dialog becomes visible
 * for a given `[commentId]` it fetches the edit history; subsequent re-opens for the same id
 * use the cached result. A different id forces a refetch.
 *
 * Each entry shows the edit timestamp, editor (with a "moderator" badge when the editor is not
 * the original author), the optional moderator reason, and the previous body rendered through
 * the same markdown pipeline as the live row.
 */
@Component({
  selector: 'tf-comment-edit-history-dialog',
  standalone: true,
  imports: [DatePipe, TranslateModule, DialogModule, ProgressSpinnerModule],
  templateUrl: './comment-edit-history-dialog.component.html',
  styleUrl: './comment-edit-history-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CommentEditHistoryDialogComponent {
  private readonly commentsService = inject(CommentsService);

  readonly visible = input<boolean>(false);
  readonly commentId = input<string | null>(null);

  readonly visibleChange = output<boolean>();

  protected readonly entries = signal<CommentEdit[]>([]);

  private readonly sanitizer = inject(DomSanitizer);

  /**
   * Template-accessible bridge to the feature-local markdown renderer; see comment-row. The
   * `bypassSecurityTrustHtml` wrapper is required because `[innerHTML]` runs Angular's own
   * DomSanitizer as a second pass, which strips `style` attributes — and we set inline styles
   * (image checkerboard, sizing) on every `<img>` post-DOMPurify. The body has already been
   * sanitized via the comments-specific allow-list in `renderCommentBody`, so bypassing the
   * second pass is safe.
   */
  protected renderBody(markdown: string): SafeHtml {
    return this.sanitizer.bypassSecurityTrustHtml(renderCommentBody(markdown));
  }
  protected readonly loading = signal(false);
  protected readonly hasError = signal(false);
  protected readonly isEmpty = computed(
    () => !this.loading() && !this.hasError() && this.entries().length === 0,
  );

  /** Last-loaded id, used to skip re-fetching on a no-op visibility toggle. */
  private loadedId: string | null = null;

  constructor() {
    effect(() => {
      const open = this.visible();
      const id = this.commentId();
      if (!open || !id) return;
      if (this.loadedId === id) return;
      void this.load(id);
    });
  }

  protected onHide(): void {
    this.visibleChange.emit(false);
  }

  /** PrimeNG drives both directions; this catches the close-via-X / Escape paths. */
  protected onVisibleChange(value: boolean): void {
    if (!value) this.visibleChange.emit(false);
  }

  private async load(id: string): Promise<void> {
    this.loading.set(true);
    this.hasError.set(false);
    this.entries.set([]);
    try {
      const history = await this.commentsService.getEditHistory(id);
      this.entries.set(history.items);
      this.loadedId = id;
    } catch {
      this.hasError.set(true);
    } finally {
      this.loading.set(false);
    }
  }
}
